import { useTranslation } from 'react-i18next';
import { EmptyState, ErrorState } from '@ui-kit';
import type { CommonCodeGroupResponse, ApiError } from '../types/code';

interface CodeGroupTreeProps {
  groups: CommonCodeGroupResponse[];
  selectedGroupCode: string | null;
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  /** Round-EXEC #397: ROLE_EXEC + !ROLE_ADMIN 컨텍스트 — system 그룹 버튼 자물쇠 처리 */
  isExecSandbox?: boolean;
  onSelect: (groupCode: string) => void;
  onAddGroup: () => void;
  onEditGroup: (group: CommonCodeGroupResponse) => void;
  onDeleteGroup: (group: CommonCodeGroupResponse) => void;
  onRetry: () => void;
}

function LockIcon({ label }: { label: string }) {
  return (
    <svg
      aria-label={label}
      role="img"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0, color: 'var(--color-warning)' }}
    >
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14H6L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4h6v2" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

/** 5행 Skeleton */
function SkeletonGroup() {
  return (
    <div
      style={{
        padding: 'var(--spacing-3) var(--spacing-4)',
        borderBottom: '1px solid var(--color-surface-border)',
      }}
    >
      <div
        style={{
          height: 14,
          width: '70%',
          borderRadius: 'var(--radius-sm)',
          background: 'var(--color-surface-elevated)',
          animation: 'pulse 1.5s ease-in-out infinite',
          marginBottom: 'var(--spacing-1)',
        }}
      />
      <div
        style={{
          height: 12,
          width: '40%',
          borderRadius: 'var(--radius-sm)',
          background: 'var(--color-surface-elevated)',
          animation: 'pulse 1.5s ease-in-out infinite',
        }}
      />
    </div>
  );
}

export function CodeGroupTree({
  groups,
  selectedGroupCode,
  isLoading,
  isError,
  error,
  isExecSandbox = false,
  onSelect,
  onAddGroup,
  onEditGroup,
  onDeleteGroup,
  onRetry,
}: CodeGroupTreeProps) {
  const { t } = useTranslation();
  const btnPrimary: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--spacing-1)',
    padding: 'var(--spacing-2) var(--spacing-3)',
    background: 'var(--color-brand-primary)',
    color: 'var(--color-text-inverse)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'var(--font-family-sans)',
    width: '100%',
    justifyContent: 'center',
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
      role="region"
      aria-label={t('commonCode.groupTree.ariaLabel')}
    >
      {/* 헤더 */}
      <div
        style={{
          padding: 'var(--spacing-3) var(--spacing-4)',
          borderBottom: '1px solid var(--color-surface-border)',
          background: 'var(--color-surface-muted)',
        }}
      >
        <h3
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 700,
            color: 'var(--color-text-secondary)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: 'var(--spacing-2)',
          }}
        >
          {t('commonCode.groupTree.title')}
        </h3>
        {/* #429: EXEC — 그룹 추가 버튼 활성 (비-system 그룹 생성 가능). EXEC 는 systemManaged=false 그룹만 추가 가능하므로 버튼 자체는 열어둠. */}
        <button
          type="button"
          onClick={onAddGroup}
          style={{
            ...btnPrimary,
          }}
          aria-label={t('commonCode.groupTree.addGroup')}
        >
          {t('commonCode.groupTree.addGroup')}
        </button>
      </div>

      {/* 그룹 목록 */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
        }}
      >
        {/* loading skeleton */}
        {isLoading && (
          <div aria-busy="true" aria-label={t('commonCode.groupTree.loadingAriaLabel')}>
            {Array.from({ length: 5 }).map((_, i) => (
              <SkeletonGroup key={i} />
            ))}
          </div>
        )}

        {/* error */}
        {isError && !isLoading && (
          <div style={{ padding: 'var(--spacing-4)' }}>
            <ErrorState
              title={t('commonCode.error.groupLoadError')}
              message={
                (error as ApiError)?.code === 'AUTH-403-001'
                  ? t('commonCode.error.groupLoadForbidden')
                  : ((error as ApiError)?.message ?? t('commonCode.error.groupLoadDefault'))
              }
              onRetry={onRetry}
            />
          </div>
        )}

        {/* empty */}
        {!isLoading && !isError && groups.length === 0 && (
          <div style={{ padding: 'var(--spacing-4)' }}>
            <EmptyState
              title={t('commonCode.groupTree.empty')}
              description={t('commonCode.groupTree.emptyHint')}
              action={
                <button type="button" onClick={onAddGroup} style={btnPrimary}>
                  {t('commonCode.groupTree.addGroup')}
                </button>
              }
            />
          </div>
        )}

        {/* 그룹 트리 항목 — R7-066: div role=button → <button> 으로 교체해 Playwright click 안정화 */}
        {!isLoading && !isError && groups.length > 0 && (
          <ul role="listbox" aria-label={t('commonCode.groupTree.selectAriaLabel')} style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {groups.map((group) => {
              const isSelected = group.groupCode === selectedGroupCode;
              return (
                <li key={group.groupCode} role="option" aria-selected={isSelected}
                  style={{
                    borderBottom: '1px solid var(--color-surface-border)',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => onSelect(group.groupCode)}
                    aria-label={t('commonCode.groupTree.groupSelectAriaLabel', { groupName: group.groupName })}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--spacing-2)',
                      padding: 'var(--spacing-3) var(--spacing-4)',
                      background: isSelected
                        ? 'color-mix(in srgb, var(--color-brand-primary) 12%, var(--color-surface-background))'
                        : 'transparent',
                      borderLeft: isSelected
                        ? '3px solid var(--color-brand-primary)'
                        : '3px solid transparent',
                      borderRight: 'none',
                      borderTop: 'none',
                      borderBottom: 'none',
                      cursor: 'pointer',
                      width: '100%',
                      textAlign: 'left',
                      fontFamily: 'inherit',
                    }}
                  >
                    {/* Lock icon for systemManaged */}
                    {group.systemManaged && <LockIcon label={t('commonCode.groupTree.systemManagedAriaLabel')} />}

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--spacing-1)',
                        }}
                      >
                        <span
                          style={{
                            fontSize: 'var(--font-size-xs)',
                            fontFamily: 'var(--font-family-mono)',
                            color: isSelected
                              ? 'var(--color-brand-primary)'
                              : 'var(--color-text-primary)',
                            fontWeight: isSelected ? 700 : 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {group.groupCode}
                        </span>
                      </div>
                      <div
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-text-secondary)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          marginTop: 2,
                        }}
                      >
                        {group.groupName}
                        {group.itemCount !== undefined && (
                          <span
                            style={{
                              marginLeft: 'var(--spacing-1)',
                              color: 'var(--color-text-muted)',
                            }}
                          >
                            ({group.itemCount})
                          </span>
                        )}
                      </div>
                    </div>

                    {/* 수정 버튼 — systemManaged=true 만 비활성 (#429).
                        EXEC 컨텍스트에서 systemManaged=true → locked tooltip, 비-system → 활성 */}
                    {(() => {
                      const isEditBlocked = group.systemManaged;
                      const editTitle = group.systemManaged && isExecSandbox
                        ? t('commonCode.exec.locked.tooltip')
                        : group.systemManaged
                          ? t('commonCode.error.systemReadonly')
                          : t('commonCode.groupTree.editTitle');
                      return (
                        <button
                          type="button"
                          aria-label={t('commonCode.groupTree.groupEditAriaLabel', { groupName: group.groupName })}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isEditBlocked) onEditGroup(group);
                          }}
                          disabled={isEditBlocked}
                          title={editTitle}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: isEditBlocked ? 'not-allowed' : 'pointer',
                            color: isEditBlocked ? 'var(--color-text-disabled, var(--color-text-muted))' : 'var(--color-text-muted)',
                            padding: 'var(--spacing-1)',
                            borderRadius: 'var(--radius-sm)',
                            display: 'inline-flex',
                            alignItems: 'center',
                            flexShrink: 0,
                            opacity: isEditBlocked ? 0.4 : 1,
                          }}
                        >
                          <EditIcon />
                        </button>
                      );
                    })()}

                    {/* 삭제 버튼 — systemManaged=true 이면 항상 숨김 (#429).
                        EXEC 의 비-system 그룹은 삭제 버튼 노출+활성. */}
                    {(() => {
                      if (group.systemManaged) return null;
                      return (
                        <button
                          type="button"
                          aria-label={t('commonCode.groupTree.groupDeleteAriaLabel', { groupName: group.groupName })}
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteGroup(group);
                          }}
                          title={t('commonCode.groupTree.deleteTitle')}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: 'var(--color-text-muted)',
                            padding: 'var(--spacing-1)',
                            borderRadius: 'var(--radius-sm)',
                            display: 'inline-flex',
                            alignItems: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <TrashIcon />
                        </button>
                      );
                    })()}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
